import type {
  AppKnowledgeSummary,
  KnowledgeSyncProviderType,
} from '@terra/shared-contracts-knowledge-sync';
import type {
  ApplicationDocumentationFile,
  DocumentationFileCategory,
  KnowledgeSourcesCardSource,
} from '@terra/ui-design-system';

export type ApplicationDocumentationUploadFileType =
  | 'DOCS'
  | 'SWAGGER'
  | 'TESTING_REPORTS';

type ApplicationFileSummary = ApplicationDocumentationFile & {
  type: string;
};

export type GroupedApplicationDocumentationFiles = {
  applicationDocumentationFiles: ApplicationDocumentationFile[];
  apiDocumentationFiles: ApplicationDocumentationFile[];
  previousTestingReportsFiles: ApplicationDocumentationFile[];
};

const UPLOAD_FILE_TYPE_BY_CATEGORY: Record<
  DocumentationFileCategory,
  ApplicationDocumentationUploadFileType
> = {
  application: 'DOCS',
  api: 'SWAGGER',
  'testing-reports': 'TESTING_REPORTS',
};

const toDocumentationFile = ({
  id,
  name,
  size,
}: ApplicationFileSummary): ApplicationDocumentationFile => ({
  id,
  name,
  size,
});

const filesOfType = ({
  files,
  fileType,
}: {
  files: ReadonlyArray<ApplicationFileSummary>;
  fileType: ApplicationDocumentationUploadFileType;
}) => files.filter((file) => file.type === fileType).map(toDocumentationFile);

export const toUploadFileType = (
  category: DocumentationFileCategory,
): ApplicationDocumentationUploadFileType =>
  UPLOAD_FILE_TYPE_BY_CATEGORY[category];

export const groupApplicationDocumentationFiles = (
  files: ReadonlyArray<ApplicationFileSummary>,
): GroupedApplicationDocumentationFiles => ({
  applicationDocumentationFiles: filesOfType({ files, fileType: 'DOCS' }),
  apiDocumentationFiles: filesOfType({ files, fileType: 'SWAGGER' }),
  previousTestingReportsFiles: filesOfType({
    files,
    fileType: 'TESTING_REPORTS',
  }),
});

export const toKnowledgeSourcesCardSources = (
  sources: ReadonlyArray<AppKnowledgeSummary>,
): KnowledgeSourcesCardSource[] =>
  sources
    .filter((source) => source.mappings.length > 0)
    .map((source) => ({
      provider: source.provider,
      scopes: source.mappings.map(({ scopeType, scopeExternalId }) => ({
        scopeType,
        scopeExternalId,
      })),
      indexedDocumentCount: source.activeIngestionLinks,
    }));

export const getProviderMappingIds = ({
  sources,
  provider,
}: {
  sources: ReadonlyArray<AppKnowledgeSummary>;
  provider: KnowledgeSyncProviderType;
}): string[] =>
  sources
    .filter((source) => source.provider === provider)
    .flatMap((source) => source.mappings.map((mapping) => mapping.id));
